import React, { useEffect, useState } from 'react';
import { useToast } from '../../ui/Toast';
import {
  listFamilyChangeRequests,
  getMembersByIds,
} from './familyChangeRequestsClient';
import FamilyRequestDetail from './FamilyRequestDetail';
import ApproveFamilyRequestDialog from './ApproveFamilyRequestDialog';
import RejectFamilyRequestDialog from './RejectFamilyRequestDialog';
import type {
  FamilyChangeRequest,
  FamilyChangeRequestStatus,
  FamilyRequestMemberSummary,
} from './types';

const kindLabel = (kind: FamilyChangeRequest['kind']): string => {
  switch (kind) {
    case 'NEW_PERSON':
      return 'Add a new person';
    case 'EXISTING_MEMBER':
      return 'Add an existing member';
    case 'REMOVE_MEMBER':
      return 'Remove a member';
    case 'PORTAL_ACCESS':
      return 'Portal login request';
    default:
      return kind;
  }
};

const statusBadgeClass = (status: FamilyChangeRequestStatus): string => {
  const styles: Record<FamilyChangeRequestStatus, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
  };
  return styles[status] || 'bg-gray-100 text-gray-800';
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Admin "Family requests" tab (#127 slice 5,
 * `.agents/tasks/127-family-requests-contract.md`, binding). Lists
 * FamilyChangeRequest rows (newest first, filterable by status), shows a
 * detail panel per the contract, and lets an admin Approve or Reject via
 * `decideFamilyChangeRequest`.
 */
const FamilyRequestsTab: React.FC = () => {
  const [requests, setRequests] = useState<FamilyChangeRequest[]>([]);
  const [members, setMembers] = useState<Map<string, FamilyRequestMemberSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<FamilyChangeRequestStatus | 'ALL'>('PENDING');
  const [pendingCount, setPendingCount] = useState(0);
  const [selected, setSelected] = useState<FamilyChangeRequest | null>(null);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const toast = useToast();

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [filtered, all] = await Promise.all([
        listFamilyChangeRequests(statusFilter),
        listFamilyChangeRequests('ALL'),
      ]);
      setRequests(filtered);
      setPendingCount(all.filter((r) => r.status === 'PENDING').length);

      const ids = new Set<string>();
      for (const r of filtered) {
        ids.add(r.primaryMemberId);
        if (r.existingMemberId) ids.add(r.existingMemberId);
        if (r.removeMemberId) ids.add(r.removeMemberId);
        if (r.newPrimaryMemberId) ids.add(r.newPrimaryMemberId);
      }
      const memberMap = await getMembersByIds(Array.from(ids));
      setMembers(memberMap);
    } catch (err: any) {
      console.error('Error loading family change requests:', err);
      setError(err?.message || 'Failed to load family requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApproved = async (message: string) => {
    setShowApprove(false);
    setSelected(null);
    toast.success(message);
    await load();
  };

  const handleRejected = async (message: string) => {
    setShowReject(false);
    setSelected(null);
    toast.success(message);
    await load();
  };

  const isPaid = (r: FamilyChangeRequest) => r.amountDueCents > 0 && !!r.paymentReceived;

  return (
    <div className="space-y-6" data-testid="family-requests-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          Family requests
          <span
            data-testid="family-requests-pending-badge"
            aria-label={`${pendingCount} pending`}
            className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-fishing-blue text-white text-xs font-semibold"
          >
            {pendingCount}
          </span>
        </h2>
        <button
          onClick={load}
          className="inline-flex items-center min-h-[44px] px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <label htmlFor="family-requests-status-filter" className="block text-sm font-medium text-gray-700 mb-2">
          Status filter
        </label>
        <select
          id="family-requests-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as FamilyChangeRequestStatus | 'ALL')}
          className="min-h-[44px] w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
        >
          <option value="ALL">All requests</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p role="alert" className="text-red-600 mb-4">
              {error}
            </p>
            <button onClick={load} className="text-fishing-blue hover:text-blue-700">
              Try Again
            </button>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <p className="mt-2 text-sm text-gray-500">No family requests found</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {requests.map((r) => {
              const requester = members.get(r.primaryMemberId);
              return (
                <li key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {kindLabel(r.kind)}
                      <span
                        className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadgeClass(r.status)}`}
                      >
                        {r.status}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {requester ? `${requester.firstName} ${requester.lastName}` : r.primaryMemberId}
                      {requester?.memberNumber ? ` (${requester.memberNumber})` : ''}
                      {' · '}
                      {formatDate(r.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelected(r)}
                    className="min-h-[44px] px-3 py-2 text-sm font-medium text-fishing-blue bg-blue-50 rounded-md hover:bg-blue-100"
                  >
                    Review
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center sm:p-4 z-40">
          <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-medium text-gray-900">Family request details</h3>
              <button
                onClick={() => setSelected(null)}
                aria-label="Close"
                className="min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-4">
              <FamilyRequestDetail request={selected} members={members} />
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setSelected(null)}
                className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              {selected.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => setShowReject(true)}
                    className="min-h-[44px] px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setShowApprove(true)}
                    className="min-h-[44px] px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-fishing-green hover:bg-green-600"
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {selected && showApprove && (
        <ApproveFamilyRequestDialog
          requestId={selected.id}
          onClose={() => setShowApprove(false)}
          onApproved={handleApproved}
        />
      )}

      {selected && showReject && (
        <RejectFamilyRequestDialog
          requestId={selected.id}
          isPaid={isPaid(selected)}
          amountDueCents={selected.amountDueCents}
          onClose={() => setShowReject(false)}
          onRejected={handleRejected}
        />
      )}
    </div>
  );
};

export default FamilyRequestsTab;
